import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './button';
import { ArrowRight, Play, Waves, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HeroCarousel } from './HeroCarousel';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionP = motion.p;

interface HeroProps {
  title: string;
  subtitle: string;
  videoSrc?: string;
  imageSrc?: string;
  ctaText1?: string;
  ctaText2?: string;
  ctaLink1?: string;
  ctaLink2?: string;
  carouselSlides?: Array<{
    id: number;
    image: string;
    title: string;
    subtitle: string;
    description?: string;
  }>;
  useCarousel?: boolean;
  autoPlayInterval?: number;
}

// Floating orb component
const FloatingOrb = ({ delay = 0, size = 'w-4 h-4', position = 'top-10 left-10' }) => (
  <MotionDiv
    className={`absolute ${position} ${size} bg-gradient-to-r from-ocean to-accent rounded-full blur-sm opacity-60`}
    animate={{
      y: [0, -20, 0],
      x: [0, 10, 0],
      scale: [1, 1.2, 1],
      opacity: [0.6, 1, 0.6],
    }}
    transition={{
      duration: 4 + delay,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay,
    }}
  />
);

// Text reveal animation component
const TextReveal = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <MotionDiv
    initial={{ y: 100, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ 
      duration: 0.8, 
      delay,
      type: "spring",
      stiffness: 100,
      damping: 10
    }}
  >
    {children}
  </MotionDiv>
);

export function Hero({
  title,
  subtitle,
  videoSrc,
  imageSrc,
  ctaText1 = "Explore Services",
  ctaText2 = "Contact Us",
  ctaLink1 = "/services",
  ctaLink2 = "/contact",
  carouselSlides,
  useCarousel = false,
  autoPlayInterval = 3000,
}: HeroProps) {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      {useCarousel && carouselSlides && carouselSlides.length > 0 ? (
        <div className="relative min-h-screen" style={{ minHeight: '100vh' }}>
          <HeroCarousel 
            slides={carouselSlides} 
            className="min-h-screen" 
            autoPlayInterval={autoPlayInterval}
          />
          
          {/* CTA Buttons Overlay for Carousel */}
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30">
            <MotionDiv
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              <MotionDiv whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  className="group bg-gradient-to-r from-ocean to-accent hover:from-ocean-dark hover:to-accent-dark text-white border-0 px-8 py-4 text-lg font-semibold rounded-full shadow-2xl transition-all duration-300"
                  asChild
                >
                  <Link to={ctaLink1}>
                    {ctaText1}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </MotionDiv>
              
              <MotionDiv whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outline"
                  size="lg"
                  className="group bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300"
                  asChild
                >
                  <Link to={ctaLink2}>
                    <Play className="h-5 w-5" />
                    {ctaText2}
                  </Link>
                </Button>
              </MotionDiv>
            </MotionDiv>
          </div>
        </div>
      ) : (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-navy via-primary to-primary-light">
      {/* Dynamic background gradient */}
      <MotionDiv
        className="absolute inset-0 bg-gradient-to-r from-ocean/20 to-accent/20"
        animate={{
          background: [
            "linear-gradient(45deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2))",
            "linear-gradient(45deg, rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.2))",
            "linear-gradient(45deg, rgba(236, 72, 153, 0.2), rgba(59, 130, 246, 0.2))",
          ]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      
      {/* Interactive gradient based on mouse position */}
      <MotionDiv
        className="absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(600px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59, 130, 246, 0.4), transparent 40%)`,
        }}
      />

      {/* Floating orbs */}
      <FloatingOrb delay={0} size="w-6 h-6" position="top-20 left-20" />
      <FloatingOrb delay={1} size="w-4 h-4" position="top-40 right-32" />
      <FloatingOrb delay={2} size="w-8 h-8" position="bottom-40 left-32" />
      <FloatingOrb delay={0.5} size="w-3 h-3" position="top-60 left-1/2" />
      <FloatingOrb delay={1.5} size="w-5 h-5" position="bottom-20 right-20" />

      {/* Background image/video */}
      {imageSrc && (
        <div className="absolute inset-0 z-0">
          <img 
            src={imageSrc} 
            alt="Hero background" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/80 to-primary/80" />
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6 sm:gap-10 py-10 sm:py-20">
        <TextReveal delay={0.2}>
          <MotionDiv
            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-3 py-1.5 sm:px-6 sm:py-3 mb-4 sm:mb-8 border border-white/20"
            whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)" }}
          >
            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            <span className="text-white/90 font-medium text-xs sm:text-base">Premier Shipping Solutions</span>
          </MotionDiv>
        </TextReveal>

        <TextReveal delay={0.4}>
          <MotionH1 
            className="text-2xl xs:text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-semibold sm:font-bold text-white mb-4 sm:mb-8 leading-snug sm:leading-tight"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #60a5fa 50%, #a855f7 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {title}
          </MotionH1>
        </TextReveal>

        <TextReveal delay={0.6}>
          <MotionP 
            className="text-xs xs:text-sm sm:text-lg md:text-2xl text-white/80 mb-6 sm:mb-12 max-w-2xl sm:max-w-4xl mx-auto leading-normal sm:leading-relaxed font-normal"
          >
            {subtitle}
          </MotionP>
        </TextReveal>

        <TextReveal delay={1.0}>
          <MotionDiv className="flex flex-col gap-3 sm:flex-row sm:gap-6 justify-center items-center w-full mt-1">
            <MotionDiv whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group w-full sm:w-auto h-10 sm:h-auto bg-gradient-to-r from-ocean to-accent hover:from-ocean-dark hover:to-accent-dark text-white border-0 px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-lg font-medium sm:font-semibold rounded-full shadow-2xl transition-all duration-300"
                asChild
              >
                <Link to={ctaLink1}>
                  {ctaText1}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </MotionDiv>
            <MotionDiv whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="group w-full sm:w-auto h-10 sm:h-auto bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-lg font-medium sm:font-semibold rounded-full transition-all duration-300"
                asChild
              >
                <Link to={ctaLink2}>
                  <Play className="h-5 w-5" />
                  {ctaText2}
                </Link>
              </Button>
            </MotionDiv>
          </MotionDiv>
        </TextReveal>

        {/* Scroll Indicator */}
        <MotionDiv
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <MotionDiv
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-white/70"
          >
            <span className="text-sm font-medium">Scroll to explore</span>
            <Waves className="h-6 w-6" />
          </MotionDiv>
        </MotionDiv>
      </div>

      {/* Animated grid overlay */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }}
      />
    </div>
      )}
    </>
  );
}

export default Hero;


